import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Divider,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import { useBusinessAdmin } from '../../contexts/BusinessAdminContext';
import { useBusinessAgent } from '../../contexts/BusinessAgentContext';
import { useActivityParser } from '../../hooks/useActivityParser';
import { ACTIVITY_TYPES } from '../../../shared/constants';

interface RecentActivityTableProps {
  maxEntries?: number;
  showLoadMore?: boolean;
  viewType?: 'admin' | 'agent'; // New prop to determine which context to use
}

const RecentActivityTable: React.FC<RecentActivityTableProps> = ({
  maxEntries = 10,
  showLoadMore = false,
  viewType = 'admin' // Default to admin view for backwards compatibility
}) => {
  // Conditionally use only the appropriate context based on viewType
  let activities, isLoadingActivities, activitiesError, activityPagination, loadMoreActivities;

  if (viewType === 'agent') {
    const agentContext = useBusinessAgent();
    ({ 
      activities, 
      isLoadingActivities, 
      activitiesError, 
      activityPagination,
      loadMoreActivities 
    } = agentContext);
  } else {
    const adminContext = useBusinessAdmin();
    ({ 
      activities, 
      isLoadingActivities, 
      activitiesError, 
      activityPagination,
      loadMoreActivities 
    } = adminContext);
  }
  
  const { parseActivities } = useActivityParser();

  const getActivityIcon = (type: number) => {
    switch (type) {
      case ACTIVITY_TYPES.CLIENT_ADDED:
        return <PersonAddIcon />;
      case ACTIVITY_TYPES.AGENT_JOINED:
        return <PeopleAltIcon />;
      case ACTIVITY_TYPES.AGENT_REMOVED:
        return <DeleteIcon />;
      case ACTIVITY_TYPES.AGENT_UPDATED:
        return <EditIcon />;
      case ACTIVITY_TYPES.HOME_ADDED:
        return <HomeIcon />;
      case ACTIVITY_TYPES.HOME_REMOVED:
        return <DeleteIcon />;
      case ACTIVITY_TYPES.BUSINESS_UPDATED:
        return <BusinessIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const getActivityColor = (type: number) => {
    switch (type) {
      case ACTIVITY_TYPES.CLIENT_ADDED:
      case ACTIVITY_TYPES.AGENT_JOINED:
      case ACTIVITY_TYPES.HOME_ADDED:
        return 'success.light';
      case ACTIVITY_TYPES.AGENT_REMOVED:
      case ACTIVITY_TYPES.CLIENT_REMOVED:
      case ACTIVITY_TYPES.HOME_REMOVED:
        return 'error.light';
      case ACTIVITY_TYPES.AGENT_UPDATED:
      case ACTIVITY_TYPES.CLIENT_UPDATED:
        return 'warning.light';
      case ACTIVITY_TYPES.BUSINESS_UPDATED:
        return 'info.light';
      default:
        return 'primary.light';
    }
  };

  const handleLoadMore = async () => {
    await loadMoreActivities();
  };

  if (activitiesError) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>Recent Activity</Typography>
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error">{activitiesError}</Typography>
        </Box>
      </Paper>
    );
  }

  const parsedActivities = parseActivities(activities);
  const displayActivities = maxEntries ? parsedActivities.slice(0, maxEntries) : parsedActivities;
  const hasMoreToShow = showLoadMore && activityPagination?.hasMore;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        {viewType === 'agent' ? 'Recent Client Activity' : 'Recent Activity'}
      </Typography>
      
      {isLoadingActivities && activities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography color="text.secondary">Loading activities...</Typography>
        </Box>
      ) : displayActivities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No recent activity</Typography>
        </Box>
      ) : (
        <>
          <List sx={{ width: '100%' }}>
            {displayActivities.map((activity, index) => (
              <Box key={activity.id}>
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemAvatar>
                    <Avatar 
                      sx={{ 
                        bgcolor: getActivityColor(activity.type),
                        width: 40,
                        height: 40
                      }}
                    >
                      {getActivityIcon(activity.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {activity.description}
                    </Typography>
                    {activity.homeAddress && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {activity.homeAddress}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {activity.formattedDate}
                      {activity.user && ` • by ${activity.user.name}`}
                    </Typography>
                  </Box>
                </ListItem>
                {index < displayActivities.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
          
          {hasMoreToShow && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleLoadMore}
                disabled={isLoadingActivities}
                startIcon={isLoadingActivities && <CircularProgress size={16} />}
              >
                {isLoadingActivities ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default RecentActivityTable;